/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { API, Auth } from 'aws-amplify';

import { getOrgDetailsByUserName, getUserByCognitoUserId } from '../../graphql/queries';
import { AWSAuthMode, AuthHeader, CognitoUserAuthenticationStatus } from '../Constants/App';
import { IGraphQLResponse } from '../Models';
import {
  clearSharedCalls,
  getLoginTokenHeaderWhenReady,
  getWebInfo,
  setLoginSetup,
  setLoginToken,
  setPendingSignIn,
  shareRecentCall,
  signedFetch,
} from '../Utilities/utils';
import { UserAuthEventInput } from '../../API';
import awsConfig from '../../Configuration/environment-config';
import { v4 as uuidv4 } from 'uuid';

interface UserExistOrganization {
  organizationId: string;
  organizationName: string;
  recStatus: string;
  dataStatus: string;
}

/** GET usermanagement/cognito-user/v2/list-by-email → { user }. */
interface UserExistDetails {
  status: string | null;
  cognitoUserId: string | null;
  isEnabled: boolean | null;
  isEmailVerified: boolean | null;
  userId: string | null;
  username: string | null;
  salutation: string | null;
  organization: UserExistOrganization | null;
}

/** The signed-in user as getOrgDetailsByUserName returned them. */
export interface SignedInUserOrgDetails {
  userId: string;
  username: string;
  salutation: string | null;
  organization: UserExistOrganization;
}

export class AuthenticationService {
  // Shared with any identical call made in the last few seconds (the login form and App.tsx both ask for it).
  static getUserByCognitoUserId(cognitoUserId: string) {
    return shareRecentCall(`getUserByCognitoUserId:${cognitoUserId}`, () =>
      AuthenticationService.fetchUserByCognitoUserId(cognitoUserId)
    );
  }

  private static async fetchUserByCognitoUserId(cognitoUserId: string) {
    try {
      const authHeaders: AuthHeader = await this.getAuthHeaders();
      const userByCognitoUserIdResponse: IGraphQLResponse = await API.graphql(
        {
          query: getUserByCognitoUserId,
          variables: { cognitoUserId },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: userByCognitoUserIdResponse?.data?.getUserByCognitoUserId ?? {}, errors: [] };
    } catch (e: any) {
      return { data: {}, errors: e?.errors ?? [] };
    }
  }

  static getSignedInUserDetails() {
    return shareRecentCall('getSignedInUserDetails', () => AuthenticationService.fetchSignedInUserDetails());
  }

  private static async fetchSignedInUserDetails(): Promise<{
    customer: { data?: SignedInUserOrgDetails; error: unknown[] };
    userId: string;
    errors: { message: string }[];
  }> {
    try {
      const idTokenPayload = (await Auth.currentSession()).getIdToken().decodePayload();
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'userExistV2');
      if (!endpointConfig?.endpoint) {
        // Not configured for this environment: use the two calls userExist v2 replaced.
        return await AuthenticationService.fetchSignedInUserDetailsV1(String(idTokenPayload['cognito:username'] ?? ''));
      }
      const user: string = idTokenPayload.email || (idTokenPayload.phone_number ?? '').replace('+', '');
      const url = new URL(endpointConfig.endpoint);
      url.searchParams.set('user', user);

      const response = await signedFetch({
        method: 'GET',
        url: url.toString(),
        requestId: uuidv4(),
        waitForLoginToken: false,
      });
      const userExistDetails = (response?.data as { user?: UserExistDetails } | null)?.user;
      if (response?.errors?.length || !userExistDetails?.userId) {
        // userExist v2 is not deployed everywhere yet (404), so fall back to the two calls it replaced.
        return await AuthenticationService.fetchSignedInUserDetailsV1(String(idTokenPayload['cognito:username'] ?? ''));
      }
      const userId = userExistDetails?.userId ?? '';
      const organization = userExistDetails?.organization;
      return {
        customer: {
          data:
            userId && organization?.recStatus === 'A'
              ? {
                  userId,
                  username: userExistDetails?.username ?? '',
                  salutation: userExistDetails?.salutation ?? null,
                  organization,
                }
              : undefined,
          error: [],
        },
        userId,
        errors: response?.errors ?? [],
      };
    } catch (e: any) {
      return { customer: { error: [] }, userId: '', errors: e?.errors ?? [{ message: e?.message }] };
    }
  }

  // The pre-userExist-v2 lookup: getOrgDetailsByUserName for the organization, getUserByCognitoUserId for the userId.
  private static async fetchSignedInUserDetailsV1(cognitoUsername: string): Promise<{
    customer: { data?: SignedInUserOrgDetails; error: unknown[] };
    userId: string;
    errors: { message: string }[];
  }> {
    const fetchOrgDetails = async () => {
      try {
        const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
        const orgDetailsResponse: IGraphQLResponse = await API.graphql(
          { query: getOrgDetailsByUserName, variables: {}, authMode: AWSAuthMode.COGNITO },
          { ...getWebInfo(), ...authHeaders }
        );
        return {
          data: orgDetailsResponse?.data?.getOrgDetailsByUserName as SignedInUserOrgDetails | undefined,
          error: [],
        };
      } catch (e: any) {
        return { data: undefined, error: e?.errors ?? [e] };
      }
    };
    const [customer, userDetails] = await Promise.all([
      fetchOrgDetails(),
      AuthenticationService.getUserByCognitoUserId(cognitoUsername),
    ]);
    const userId: string = (userDetails?.data as { userId?: string })?.userId ?? '';
    return { customer, userId, errors: userDetails?.errors ?? [] };
  }

  // Every authenticated GraphQL call awaits this, so it also carries the loginToken once SIGN_IN has returned it.
  // It must not wait for the whole login setup, because the setup's own calls use it.
  static async getAuthHeaders() {
    const session = await Auth.currentSession();
    return {
      Authorization: session.getIdToken().getJwtToken(),
      ...(await getLoginTokenHeaderWhenReady(false)),
    };
  }

  /**
   * Records SIGN_IN / SIGN_OUT against the user. On SIGN_IN the backend returns a loginToken for
   * this session, which is stored and then sent by signedFetch on every request.
   */
  static async createUserAuthEvent(userAuthEventInput: UserAuthEventInput, orgId?: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'createUserAuthEvent');
      if (!endpointConfig?.endpoint) {
        return { data: {}, errors: [{ message: 'Endpoint not found' }] };
      }

      const createUserAuthEventResponse = await signedFetch({
        method: 'POST',
        url: new URL(endpointConfig.endpoint).toString(),
        body: userAuthEventInput,
        requestId: uuidv4(),
        abnHeaders: {
          userid: userAuthEventInput?.userId ?? '',
          orgid: orgId ?? '',
          devicetype: 'D',
        },
        waitForLoginToken: false,
      });

      const authEventData = createUserAuthEventResponse?.data as { loginToken?: string } | null;
      if (authEventData?.loginToken) {
        setLoginToken(authEventData.loginToken);
      }
      return { data: createUserAuthEventResponse?.data ?? {}, errors: createUserAuthEventResponse?.errors ?? [] };
    } catch (e: any) {
      return { data: {}, errors: e?.errors ?? [] };
    }
  }

  // The SIGN_IN already sent in this page load. Both the App.tsx Hub handler and OAuthCallback
  // send it (for SSO, the Hub event can fire before App mounts its listener), so the second caller
  // for the same user reuses the first instead of opening a second backend session.
  // Reset on sign-out so a later login in the same page load sends a fresh one.
  static signInAuthEvent: { userId: string; promise: Promise<void> } | null = null;

  /**
   * Sends SIGN_IN so the backend opens a session and hands back the loginToken. Call it as early
   * as possible after sign-in so the requests that follow carry the token. Never throws.
   */
  static sendSignInAuthEvent(userId: string, orgId?: string): Promise<void> {
    if (!userId) return Promise.resolve();
    if (AuthenticationService.signInAuthEvent?.userId === userId) {
      return AuthenticationService.signInAuthEvent.promise;
    }
    const promise = (async () => {
      try {
        await AuthenticationService.createUserAuthEvent(
          {
            authEventType: CognitoUserAuthenticationStatus.SignIn,
            authEventTimeStamp: new Date().toJSON(),
            userId,
          },
          orgId
        );
      } catch (e) {
        console.error('SIGN_IN auth event failed', e);
      }
    })();
    AuthenticationService.signInAuthEvent = { userId, promise };
    setPendingSignIn(promise);
    return promise;
  }

  static resetSignInAuthEvent() {
    AuthenticationService.signInAuthEvent = null;
    AuthenticationService.loginCompletion = null;
    clearSharedCalls();
    setLoginSetup(null);
    setPendingSignIn(null);
  }

  static trackLoginSetup<T>(setup: Promise<T>): Promise<T> {
    setLoginSetup(setup);
    return setup;
  }

  private static loginCompletion: Promise<boolean> | null = null;

  static trackLoginCompletion(completion: Promise<boolean>): Promise<boolean> {
    AuthenticationService.loginCompletion = completion;
    return completion;
  }

  static async waitForLoginCompletion(): Promise<boolean> {
    try {
      return (await AuthenticationService.loginCompletion) ?? false;
    } catch {
      return false;
    }
  }

  static async updateCustomUserId(user: object, userId: string): Promise<boolean> {
    if (!userId) return false;
    try {
      await Auth.updateUserAttributes(user, {
        'custom:UserId': userId,
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  static async fetchAllProcesses(userId: string, orgId: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'getAllProcesses');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig.endpoint);

      const response = await signedFetch({
        method: 'GET',
        url: url.toString(),
        requestId: uuidv4(),
        abnHeaders: {
          userid: userId,
          orgid: orgId,
          devicetype: 'D',
          userlanguage: 'en',
        },
      });

      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (e: any) {
      console.error(`Error occured while fetching processes`, e);
    }
  }
}
